import { Children, ReactNode, useEffect, useRef, useState } from 'react';
import useRuntime from '@belong/providers/runtime';
import { ITransitionProps } from './Carousel.types';

export const useViewModel = (children: ReactNode[], initialSlide: number | undefined) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const slidesRef = useRef<HTMLUListElement>(null);

  const dragStartX = useRef<number>(0);
  const dragStartY = useRef<number>(0);
  const currentTranslateX = useRef<number>(0);
  const startTimestamp = useRef(0);

  const [currentSlide, setCurrentSlide] = useState<number>(initialSlide || 0);

  useEffect(() => {
    if (initialSlide) {
      setCurrentSlide(initialSlide);
    }
  }, [initialSlide]);

  const { isDesktopViewport, currentBreakpoint } = useRuntime();
  const totalSlides = Children.count(children);
  const totalColumns = 12;
  const isXSViewport = currentBreakpoint === 'xs';

  let slideColumns: number;
  if (isXSViewport) {
    slideColumns = 12;
  } else if (isDesktopViewport) {
    slideColumns = 5;
  } else {
    slideColumns = 10;
  }

  const columnWidth = containerRef.current && containerRef.current.offsetWidth / totalColumns;

  const transitionProps: ITransitionProps = {
    property: 'transform',
    ease: `cubic-bezier(0.075, 0.82, 0.165, 1)`,
    duration: 0.4
  };

  useEffect(() => {
    addEventListeners();
    return () => {
      removeEventListeners();
    };
    // these state values are used in the event listeners
  }, [isDesktopViewport, currentSlide]);

  useEffect(() => {
    const focusSlide = slidesRef.current?.childNodes[currentSlide] as HTMLElement | undefined;
    focusSlide?.focus();
  }, [currentSlide]);

  const addEventListeners = () => {
    const slidesRefCurrent = slidesRef.current;

    removeEventListeners();

    if (slidesRefCurrent) {
      slidesRefCurrent.addEventListener('transitionend', () => {
        currentTranslateX.current = getCurrentTranslateX();
      });

      slidesRefCurrent.addEventListener('touchstart', startDragSlides, { passive: true });
      slidesRefCurrent.addEventListener('touchend', endDragSlides);
      slidesRefCurrent.addEventListener('touchcancel', endDragSlides);
    }
  };

  const removeEventListeners = () => {
    const slidesRefCurrent = slidesRef.current;

    if (slidesRefCurrent) {
      slidesRefCurrent.removeEventListener('transitionend', () => {
        currentTranslateX.current = getCurrentTranslateX();
      });

      slidesRefCurrent.removeEventListener('touchstart', startDragSlides);
      slidesRefCurrent.removeEventListener('touchend', endDragSlides);
      slidesRefCurrent.removeEventListener('touchcancel', endDragSlides);
    }
  };

  const unintendedDrag = (e: TouchEvent) => {
    // no drag if the intention is to scroll
    const currentY = e.changedTouches ? e.changedTouches[0].clientY : e.touches[0].clientY;
    const isYMovementIntentional = Math.abs(currentY - dragStartY.current) > 100;

    const currentX = e.changedTouches ? e.changedTouches[0].clientX : e.touches[0].clientX;
    const isXMovementIntentional = Math.abs(currentX - dragStartX.current) > 50;

    const timeElapsed = performance.now() - startTimestamp.current;

    if (timeElapsed < 50 || isYMovementIntentional) {
      // likely a tap or a vertical scroll
      return true;
    }

    // sufficient horizontal movement to be considered a drag
    // so no unintendedDrag - return false
    return !isXMovementIntentional;
  };

  const getDragConstraints = () => {
    // create a boudary for the dragging events
    return slidesRef.current
      ? {
          left:
            ((totalSlides - 1) * ((slideColumns / totalColumns) * slidesRef.current.offsetWidth) -
              ((totalColumns - slideColumns) / totalColumns) * slidesRef.current.offsetWidth) *
            -1,
          right: 0
        }
      : { left: 0, right: 0 };
  };

  const startDragSlides = (e: TouchEvent) => {
    // add event listener to capture touchmove and set the drag start position
    dragStartX.current = e.touches[0].clientX;
    dragStartY.current = e.touches[0].clientY;
    startTimestamp.current = performance.now();
    document.addEventListener('touchmove', onDragSlides);
  };

  const endDragSlides = (e: TouchEvent) => {
    if (unintendedDrag(e)) {
      document.removeEventListener('touchmove', onDragSlides);
      return;
    }

    // remove touch move event listener
    document.removeEventListener('touchmove', onDragSlides);

    if (slidesRef.current) {
      slidesRef.current.style.transform = '';
    }

    if (e.changedTouches[0].clientX < dragStartX.current) {
      incrementSlide();
    } else {
      decrementSlide();
    }
  };

  const onDragSlides = (e: TouchEvent) => {
    if (unintendedDrag(e)) {
      return;
    }

    // drag the slides
    if (slidesRef.current) {
      const dragDistance = dragStartX.current - e.touches[0].clientX;

      if (dragDistance < 50) {
        return;
      }

      const slideDestination = currentTranslateX.current - dragDistance;

      let limitedSlideDestination = slideDestination;

      const { left: maxLeft, right: maxRight } = getDragConstraints();

      if (slideDestination < maxLeft - 150) {
        limitedSlideDestination = maxLeft;
      }
      if (slideDestination > maxRight + 150) {
        limitedSlideDestination = maxRight;
      }

      const prefersReducedMotion = window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

      if (!prefersReducedMotion) {
        // only animate if the user has not set their device to reduce motion
        slidesRef.current.style.transform = `translateX(${limitedSlideDestination}px)`;
      }
    }
  };

  const decrementSlide = () => {
    // decrement the current slide
    if (isDesktopViewport) {
      const decrementVal = currentSlide === totalSlides - 1 ? 2 : 1;
      setCurrentSlide(prevSlide => (prevSlide - 1 < 0 ? 0 : prevSlide - decrementVal));
    } else {
      setCurrentSlide(prevSlide => (prevSlide - 1 < 0 ? 0 : prevSlide - 1));
    }
  };

  const incrementSlide = () => {
    // increment the current slide
    if (isDesktopViewport) {
      setCurrentSlide(prevSlide => (prevSlide + 2 >= totalSlides - 1 ? totalSlides - 1 : prevSlide + 1));
    } else {
      setCurrentSlide(prevSlide => (prevSlide + 1 >= totalSlides - 1 ? totalSlides - 1 : prevSlide + 1));
    }
  };

  const getCurrentTranslateX = () => {
    // grab the cuurent value of the translateX property
    if (slidesRef.current) {
      const computedStyle = getComputedStyle(slidesRef.current);
      const { transform } = computedStyle;
      const matrix = new DOMMatrixReadOnly(transform);
      return matrix.m41;
    }
    return 0;
  };

  const getXOffset = () => {
    // helper function to calculate the offset position of the slides
    if (isXSViewport) {
      return 0;
    }
    return currentSlide !== 0 && columnWidth ? (columnWidth / containerRef.current.offsetWidth) * 100 : 0;
  };

  const getXTransition = () => {
    return {
      transform:
        currentSlide < totalSlides - 1
          ? `-${currentSlide * ((slideColumns / totalColumns) * 100) - getXOffset()}%`
          : `-${((currentSlide * slideColumns - totalColumns + slideColumns) / totalColumns) * 100}%`,
      transition: `${transitionProps.property} ${transitionProps.duration}s ${transitionProps.ease}`
    };
  };

  const getSlideOpacityTransition = (idx: number) => {
    let desktopOpaqueSlide: boolean;
    if (currentSlide < totalSlides - 1) {
      desktopOpaqueSlide = currentSlide === idx || currentSlide + 1 === idx;
    } else {
      desktopOpaqueSlide = currentSlide === idx || currentSlide - 1 === idx;
    }

    return isDesktopViewport
      ? {
          opacity: desktopOpaqueSlide ? 1 : 0.35,
          transition: { duration: transitionProps.duration, ease: transitionProps.ease }
        }
      : {
          opacity: currentSlide === idx ? 1 : 0.35,
          transition: { duration: transitionProps.duration, ease: transitionProps.ease }
        };
  };

  const getProgressWidth = () => {
    const widthDivider = isDesktopViewport ? totalSlides - 1 : totalSlides;
    return containerRef?.current ? 100 / widthDivider : 0;
  };

  const handleKeydown = (event: React.KeyboardEvent<HTMLUListElement>): void => {
    if (event.key === 'ArrowRight') {
      incrementSlide();
    }
    if (event.key === 'ArrowLeft') {
      decrementSlide();
    }
  };

  return {
    containerRef,
    slidesRef,
    dragStartX,
    currentSlide,
    totalSlides,
    totalColumns,
    slideColumns,
    decrementSlide,
    incrementSlide,
    getXTransition,
    getSlideOpacityTransition,
    getProgressWidth,
    handleKeydown
  };
};
